class ProductMediaWatches extends window.BAO.CustomElement {
  constructor () {
    super()

    // Button elements
    this.buttons = this.querySelectorAll('[data-image-button]')
  }

  connectedCallback () {
    super.connectedCallback()

    // Data
    this.activeImage = null
    this.data = JSON.parse(this.dataset.productWatchImagery)
    this.currentVariantId = this.dataset.currentVariantId
    this.currentData = this.data[this.currentVariantId]

    this.setupListeners()
  }

  setupListeners () {
    // Button click events
    this.updateImagery = this.updateImagery.bind(this)
    this.buttons.forEach(button => {
      button.addEventListener('click', e => {
        this.updateImagery(e)
      })
    })

    // Variant change event
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.VARIANT_CHANGE,
      e => {
        if (!e.detail.data.id) return
        this.currentVariantId = e.detail.data.id
        this.currentData = this.data[this.currentVariantId]
        this.updateElements()
      }
    )

    // Theme Light/Dark style switch event
    window.BAO.eventBus.addEventListener(window.BAO.EVENTS.THEME_SWITCH, e => {
      if (!e.detail.currentStyle) return
      this.updateImagery(undefined, this.findBestImageType())
    })

    // Load event – Load all images after page load
    window.addEventListener('load', () => {
      this.loadAllImages()
    })
  }

  updateElements () {
    this.updateImages()
    this.updateControls()

    document.dispatchEvent(new CustomEvent('product-image-updated'))
  }

  updateControls () {
    this.updateControlsActiveState()
    this.updateControlsDisabledState()
  }

  updateImagery (e, buttonType = null) {
    const button = e?.target
    const images = this.querySelectorAll('[data-image-element]')

    if (buttonType === null) {
      if (button.dataset.imageButton === 'front') {
        buttonType = this.findBestImageType()
      } else {
        buttonType = button.dataset.imageButton
      }
    }

    if (!images) return

    images.forEach(image => {
      const imageType = image.getAttribute('data-image-element')
      if (imageType === buttonType) {
        image.parentElement.setAttribute('data-active', true)
        this.activeImage = image
      } else {
        image.parentElement.removeAttribute('data-active')
      }
    })

    this.updateControls()
  }

  updateImages () {
    const defaultImageKey = this.findBestImageType()
    for (const [key, value] of Object.entries(this.currentData)) {
      const imageContainer = this.querySelector(
        `[data-image-container="${key}"]`
      )

      if (imageContainer !== null) {
        if (value.exists) {
          // Build image markup with image source and type
          let imageMarkup = this.imageMarkup
          imageMarkup = imageMarkup
            .replaceAll('[src]', value.src)
            .replaceAll('[imageType]', key)
            .replaceAll('[thumbnail]', value.thumbnail)
            .replaceAll('[imageWidth]', value.width)
            .replaceAll('[imageHeight]', value.height)
            .replaceAll('[activeState]', (key === defaultImageKey).toString())
            .replaceAll('[hiddenState]', 'false') // Not hidden if it exists

          // Add image to container
          imageContainer.outerHTML = imageMarkup

          if (key === defaultImageKey) {
            this.activeImage = this.querySelector(
              '[data-image-element][data-active="true"]'
            )
          }
        } else {
          // If the image doesn't exist, mark as hidden
          let imageMarkup = this.imageMarkup
          imageMarkup = imageMarkup
            .replaceAll('[src]', '') // Empty src
            .replaceAll('[imageType]', key)
            .replaceAll('[thumbnail]', '') // No thumbnail
            .replaceAll('[imageWidth]', '')
            .replaceAll('[imageHeight]', '')
            .replaceAll('[activeState]', 'false')
            .replaceAll('[hiddenState]', 'true') // Hidden if it doesn't exist

          // Add hidden markup to container
          imageContainer.outerHTML = imageMarkup
        }
      }
    }
  }

  findBestImageType () {
    // Finds best image to display based on current theme and available images
    const isDarkMode = document.body.classList.contains('thm-Style-dark')
    const data = this.currentData

    if (isDarkMode && data['frontDark'] && data['frontDark'].exists) {
      return 'frontDark'
    } else if (data['front'] && data['front'].exists) {
      return 'front'
    } else if (data['side'] && data['side'].exists) {
      return 'side'
    } else if (data['back'] && data['back'].exists) {
      return 'back'
    } else if (data['frontDark'] && data['frontDark'].exists) {
      return 'frontDark'
    } else {
      return false
    }
  }

  updateControlsActiveState () {
    // Update active state of buttons based on selected image
    if (!this.activeImage) return
    let imageType = this.activeImage.getAttribute('data-image-element')
    if (imageType === 'frontDark') {
      imageType = 'front'
    }

    this.buttons.forEach(button => {
      if (button.getAttribute('data-image-button') === imageType) {
        button.setAttribute('data-active', true)
      } else {
        button.removeAttribute('data-active')
      }
    })
  }

  updateControlsDisabledState () {
    // Hide button if no image is available
    let enabledButtonsCount = 0

    this.buttons.forEach(button => {
      const imageType = button.getAttribute('data-image-button')
      const data = this.currentData[imageType]

      if (data && data.exists) {
        button.removeAttribute('disabled')
        enabledButtonsCount++
      } else {
        button.setAttribute('disabled', true)
      }
    })

    // If only one button is enabled, hide it also
    if (enabledButtonsCount === 1) {
      const activeButton = this.querySelector(
        '[data-image-button]:not([disabled])'
      )
      if (activeButton) activeButton.setAttribute('disabled', true)
    }
  }

  loadAllImages () {
    // After entire page load, we load in all images to remove any delay on image switch
    for (const variantId in this.data) {
      const images = this.data[variantId]
      for (const imageType in images) {
        const imageData = images[imageType]
        if (imageData && imageData.exists && imageData.src) {
          const image = new Image()
          image.src = imageData.src
        }
      }
    }
  }

  get imageMarkup () {
    return `
      <a
        href="[src]"
        data-thumb-image="[thumbnail]"
        class="prd-MediaWatches_ImageWrapper"
        data-product-photoswipe-el="link"
        data-image-container="[imageType]"
        target="_blank"
        data-pswp-width="[imageWidth]"
        data-pswp-height="[imageHeight]"
        aria-hidden="[hiddenState]"
        data-active="[activeState]"
      >
        <img
          src="[src]"
          alt="${this.dataset.productTitle}"
          width="[imageWidth]"
          height="[imageHeight]"
          class="prd-MediaWatches_Image"
          data-image-element="[imageType]"
        >
      </a>
    `
  }
}

if (!customElements.get('product-media-watches')) {
  customElements.define('product-media-watches', ProductMediaWatches)
}
